/* eslint-disable eslint-comments/disable-enable-pair */
/* eslint-disable react/prop-types */
import React, { useEffect } from 'react';
import { useEditor, EditorContent, Node, mergeAttributes } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import { Button, Dropdown, Tag, Tooltip } from 'antd';
import {
	BoldOutlined,
	ItalicOutlined,
	OrderedListOutlined,
	UnorderedListOutlined,
	AlignCenterOutlined,
} from '@ant-design/icons';

const CHIP_STYLE =
	'display:inline-block;background:#e6f4ff;color:#1677ff;border:1px solid #91caff;' +
	'border-radius:4px;padding:0 6px;font-family:monospace;font-size:12px;' +
	'line-height:20px;user-select:all;cursor:default;margin:0 1px;';

const VariableNode = Node.create({
	name: 'variable',
	group: 'inline',
	inline: true,
	atom: true,
	addAttributes() {
		return { name: { default: null } };
	},
	parseHTML() {
		return [{ tag: 'span[data-variable]', getAttrs: (el) => ({ name: el.getAttribute('data-variable') }) }];
	},
	renderHTML({ node, HTMLAttributes }) {
		return ['span', mergeAttributes(HTMLAttributes, { 'data-variable': node.attrs.name }), `\${${node.attrs.name}}`];
	},
	addNodeView() {
		return ({ node: n }) => {
			const dom = document.createElement('span');
			dom.style.cssText = CHIP_STYLE;
			dom.setAttribute('contenteditable', 'false');
			dom.textContent = n.attrs.name;
			return { dom };
		};
	},
});

const preprocessHtml = (html) =>
	html.replace(/\$\{(\w+)\}/g, (_, name) => `<span data-variable="${name}">\${${name}}</span>`);

const stripVariableSpans = (html) =>
	html.replace(/<span[^>]*data-variable="(\w+)"[^>]*>[^<]*<\/span>/g, (_, name) => `\${${name}}`);

const ToolbarButton = ({ onClick, active, title, children }) => (
	<Tooltip title={title}>
		<Button
			size='small'
			type={active ? 'primary' : 'text'}
			onClick={onClick}
			style={{ marginRight: 2 }}>
			{children}
		</Button>
	</Tooltip>
);

const TipTapEditor = ({ value, onChange, variables = [], readOnly = false }) => {
	const editor = useEditor({
		extensions: [StarterKit, VariableNode, TextAlign.configure({ types: ['heading', 'paragraph'] })],
		content: preprocessHtml(value || ''),
		editable: !readOnly,
		onUpdate: ({ editor: ed }) => {
			if (onChange) onChange(stripVariableSpans(ed.getHTML()));
		},
	});

	useEffect(() => {
		if (editor && value !== undefined) {
			const currentClean = stripVariableSpans(editor.getHTML());
			if (currentClean !== (value || '')) {
				editor.commands.setContent(preprocessHtml(value || ''), false);
			}
		}
	}, [value, editor]);

	const insertVariable = (varName) => {
		if (editor) {
			editor.chain().focus().insertContent({ type: 'variable', attrs: { name: varName } }).run();
		}
	};

	const variableMenuItems = variables.map((v) => ({
		key: v,
		label: <Tag color='blue' style={{ fontFamily: 'monospace', cursor: 'pointer' }}>{v}</Tag>,
		onClick: () => insertVariable(v),
	}));

	if (!editor) return null;

	return (
		<div style={{ border: '1px solid #d9d9d9', borderRadius: 6, overflow: 'hidden' }}>
			{!readOnly && (
				<div
					style={{
						padding: '6px 8px',
						borderBottom: '1px solid #d9d9d9',
						background: '#fafafa',
						display: 'flex',
						alignItems: 'center',
						gap: 2,
						flexWrap: 'wrap',
					}}>
					<ToolbarButton
						title='Bold'
						onClick={() => editor.chain().focus().toggleBold().run()}
						active={editor.isActive('bold')}>
						<BoldOutlined />
					</ToolbarButton>
					<ToolbarButton
						title='Italic'
						onClick={() => editor.chain().focus().toggleItalic().run()}
						active={editor.isActive('italic')}>
						<ItalicOutlined />
					</ToolbarButton>
					<ToolbarButton
						title='Heading 1'
						onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
						active={editor.isActive('heading', { level: 1 })}>
						H1
					</ToolbarButton>
					<ToolbarButton
						title='Heading 2'
						onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
						active={editor.isActive('heading', { level: 2 })}>
						H2
					</ToolbarButton>
					<ToolbarButton
						title='Bullet List'
						onClick={() => editor.chain().focus().toggleBulletList().run()}
						active={editor.isActive('bulletList')}>
						<UnorderedListOutlined />
					</ToolbarButton>
					<ToolbarButton
						title='Ordered List'
						onClick={() => editor.chain().focus().toggleOrderedList().run()}
						active={editor.isActive('orderedList')}>
						<OrderedListOutlined />
					</ToolbarButton>
					<ToolbarButton
						title='Center'
						onClick={() => editor.chain().focus().toggleTextAlign('center').run()}
						active={editor.isActive({ textAlign: 'center' })}>
						<AlignCenterOutlined />
					</ToolbarButton>
					{variables.length > 0 && (
						<Dropdown menu={{ items: variableMenuItems }} trigger={['click']}>
							<Button size='small' type='dashed' style={{ marginLeft: 8 }}>
								{'{ } Insert Variable'}
							</Button>
						</Dropdown>
					)}
				</div>
			)}
			<EditorContent
				editor={editor}
				style={{
					minHeight: 200,
					padding: '8px 12px',
					fontSize: 14,
					lineHeight: 1.6,
				}}
			/>
		</div>
	);
};

export default TipTapEditor;
